import React, { createRef } from "react";
import {
  withStyles,
  Popover,
  TableRow,
  Menu,
  MenuItem,
  ListSubheader,
  Divider,
  FormControlLabel,
  Checkbox,
  ListItemIcon,
  Typography,
  MenuList,
} from "@material-ui/core";
import red from "@material-ui/core/colors/red";
import { fade } from "@material-ui/core/styles/colorManipulator";
import { Check, Filter, FilterOutline, TableArrowDown } from "mdi-material-ui";
import EnhancedTable from "@wa/werkstoff-data-table";
import withLocalStorageConfig from "@wa/werkstoff-data-table/lib/withLocalStorageConfig";
import withFixedColumns from "@wa/werkstoff-data-table/lib/withFixedColumns";
import withVerticalScroll from "@wa/werkstoff-data-table/lib/withVerticalScroll";
import StringSearchFilter from "@wa/werkstoff-data-table/lib/FilterPopover/StringSearchFilter";
import cx from "classnames";
import debounce from "debounce";
import ChangeStockForm from "./ChangeStockForm";
import { useRegistry, withRegistry } from "../../plugins/registry";
import TooltipIconButton from "../../../components/TooltipIconButton";
import PopoverIconButton from "../../../components/PopoverIconButton";
import TableExportForm from "./TableExportForm";
import { saveCsv, saveXlsx } from "../../../util/spreadsheetExport";
import { withSnackbar } from "material-ui-snackbar-provider";
import {
  compartmentColumn,
  ifStockItem,
  loadCarrierColumn,
  noteColumn,
  towerColumn,
  maybeAggregated,
  emptyToBottom,
} from "./stockTable";
import ComponentExtensionPoint from "../../plugins/ComponentExtensionPoint";
import ChangeTemporaryLocationForm from "../containers/ChangeTemporaryLocationForm";
import SearchTextField from "../../../components/SearchTextField";
import DraggablePaper from "../../../components/DraggablePaper";
import ChangeLocationForm from "../containers/ChangeLocationForm";
import MoveLoadCarrierForm from "../containers/MoveLoadCarrierForm";
import CreateStockItemForm from "./CreateStockItemForm";
import TablePlaceholder from "../../../components/TablePlaceholder";
import { Trans, useTranslation, withTranslation } from "react-i18next";
import { prepareSmartSearch } from "../../../util/smartSearch";
import ToggleButton from "../../../components/ToggleButton";

const StockTable = withLocalStorageConfig()(
  withFixedColumns(2)(withVerticalScroll()(EnhancedTable))
);

const stockItemNoteColumn = (other, { t }) => {
  const column = noteColumn({
    defaultTitle: (index) =>
      t("Bestands-Notiz {{number}}", { number: index + 1 }),
    note: maybeAggregated((row) => {
      switch (other.noteIndex) {
        case 0:
          return row.note;
        case 1:
          return row.note2;
        case 2:
          return row.note3;
        default:
          return null;
      }
    }),
    itemId: (row) => row.id,
    type: "stockItem",
    ...other,
  });
  return {
    ...column,
    content: ifStockItem(column.content),
    contentString: ifStockItem(column.contentString),
    tooltip: ifStockItem(column.tooltip),
  };
};

const articleNoteColumn = (other, { t }) =>
  noteColumn({
    defaultTitle: (index) =>
      t("Artikel-Notiz {{number}}", { number: index + 1 }),
    note: (row) => {
      switch (other.noteIndex) {
        case 0:
          return row.note;
        case 1:
          return row.note2;
        case 2:
          return row.note3;
        default:
          return null;
      }
    },
    itemId: (row) => row.article.id,
    type: "article",
    ...other,
  });

const styles = (theme) => ({
  root: {
    padding: "24px 24px 14px",
    flex: 1,
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
  },
  toolbar: {
    marginBottom: 16,
    display: "flex",
    flexDirection: "row",
  },
  search: {
    width: 350,
  },
  flex: {
    flex: 1,
  },
  toolbarButton: {
    margin: "-4px 0",
    padding: 10,
  },
  deltaPopover: {
    padding: 16,
    marginTop: -8,
    marginLeft: 24,
    minWidth: 450,
    maxWidth: 500,
    "&$popoverDragging": {
      transition: "opacity 355ms cubic-bezier(0.4, 0, 0.2, 1) 0ms !important",
    },
    "& > *:first-child": {
      cursor: "default",
    },
  },
  popoverDragging: {},
  popover: {
    padding: 8,
  },
  exportPopover: {
    padding: 8,
    width: 400,
  },
  aggregateExport: {
    margin: "0 -6px",
  },
  minimumStockRow: {
    "& > td:first-child": {
      background: `linear-gradient(to right, ${red[500]} 0, ${red[500]} 4px, #fff 4px) !important`,
    },
    "&&&:hover > td:first-child": {
      // don't let the table's row hover style overwrite the red border
      background: `linear-gradient(to right, ${red[500]} 0, ${
        red[500]
      } 4px, ${fade(
        theme.palette.type === "light"
          ? "rgba(0, 0, 0, 0.07)"
          : "rgba(255, 255, 255, 0.14)",
        0
      )} 4px, ${fade(
        theme.palette.type === "light"
          ? "rgba(0, 0, 0, 0.07)"
          : "rgba(255, 255, 255, 0.14)",
        0
      )}) !important`,
    },
  },
  menuHeader: {
    outline: "none",
    height: 32,
    lineHeight: "32px",
    pointerEvents: "none",
    userSelect: "none",
  },
  table: {
    flex: 1,
  },
});

export const defaultQuickFilters = [
  {
    id: "hideEmptyStockItems",
    label: ({ t }) => t("Leere Bestände ausblenden"),
    filter: ({ row, getStock }) => Math.floor(getStock(row) * 1000) > 0,
  },
];

function StockPlaceholder({ tab, error, type }) {
  const registry = useRegistry();
  const { t } = useTranslation();

  return error ? (
    <>
      {t("Beim Laden des Bestands ist ein Fehler aufgetreten.")}
      <br />
      <br />
      {error.graphQLErrors?.length === 0 &&
        t("Bitte überprüfen Sie Ihre Internetverbindung.")}
    </>
  ) : tab === "active" ? (
    type === registry.stockItemTypes[type].articleType ? (
      <>
        <span>
          {t("Es gibt keine aktiven {{type}}-Artikel.", {
            type: registry.stockItemTypes[type].displayName(1, { t }),
            context: type,
          })}
          <br />
          <br />
          {t(
            "Wechseln Sie in die Artikel-Ansicht, um neue Artikel anzulegen. Anschließend können die Bestände hier verwaltet werden."
          )}
        </span>
      </>
    ) : (
      <span>
        {t("Es gibt derzeit keine {{type}}.", {
          type: registry.stockItemTypes[type].displayName(2, { t }),
        })}
        <br />
        <br />
        {t(
          "Wechseln Sie in die Bestands-Ansicht, um neue {{type}} anzulegen.",
          {
            type: registry.stockItemTypes[type].displayName(2, { t }),
          }
        )}
      </span>
    )
  ) : (
    <>
      {t("Es gibt keine archivierten {{type}}-Bestände.", {
        type: registry.stockItemTypes[type].displayName(1, { t }),
        context: type,
      })}
    </>
  );
}

const defaultActions = [
  {
    id: "stock",
    items: [
      {
        label: ({ t }) => t("Bestand verändern"),
        action: "changeStock",
      },
    ],
    showIf: ({ selectedItem }) => selectedItem?.__typename === "StockItem",
  },
  {
    id: "stockActions",
    items: [],
    showIf: ({ selectedItem }) => selectedItem?.__typename === "StockItem",
  },
  {
    id: "loadCarrier",
    items: [
      {
        label: ({ t }) => t("Ladungsträger bewegen"),
        action: {
          PopoverForm: MoveLoadCarrierForm,
        },
        disableIf: ({ selectedItem }) => selectedItem?.loadCarrier == null,
        showIf: ({ features }) => features.loadCarriers,
      },
      {
        label: ({ t }) => t("Lagerort/Ladungsträger ändern"),
        action: {
          PopoverForm: ChangeLocationForm,
        },
        showIf: ({ features }) => features.loadCarriers,
      },
      {
        label: ({ t }) => t("Lagerort ändern"),
        action: {
          PopoverForm: ChangeLocationForm,
        },
        showIf: ({ features }) => !features.loadCarriers,
      },
      {
        label: ({ t }) => t("Temporären Lagerort ändern"),
        action: {
          PopoverForm: ChangeTemporaryLocationForm,
        },
      },
    ],
    showIf: ({ selectedItem }) => selectedItem?.__typename === "StockItem",
  },
  {
    id: "stockItems",
    items: [
      {
        showIf: ({ stockItemType }) =>
          stockItemType.stockActionsConfig?.canCreateStockItems !== false,
        label: ({ t, stockItemType, type }) =>
          t("{{type}}-Einheit hinzufügen", {
            type: stockItemType.displayName(1, { t }),
            context: type,
          }),
        action: "createStockItem",
      },
      {
        showIf: ({ stockItemType, selectedItem }) =>
          stockItemType.stockActionsConfig?.canCreateStockItems !== false &&
          selectedItem?.__typename === "StockItem",
        label: ({ t, stockItemType, type }) =>
          t("{{type}}-Einheit kopieren", {
            type: stockItemType.displayName(1, { t }),
            context: type,
          }),
        action: "copyStockItem",
      },
      {
        showIf: ({ stockItemType, selectedItem }) =>
          stockItemType.stockActionsConfig?.canHideStockItems !== false &&
          selectedItem?.__typename === "StockItem",
        disableIf: ({ getStock, selectedItem }) => getStock(selectedItem) > 0,
        label: ({ t, stockItemType, type }) =>
          t("{{type}}-Einheit entfernen", {
            type: stockItemType.displayName(1, { t }),
            context: type,
          }),
        action: "hideItem",
      },
    ],
  },
  {
    id: "articleActions",
    items: [],
    showIf: ({ selectedItem }) =>
      selectedItem?.__typename === "AggregatedStockItems",
  },
];

function getStockActions(
  actions,
  {
    selectedItem,
    getStock,
    t,
    features,
    registry,
    type,
    handleItemAction,
    handleHidePopover,
  }
) {
  if (!selectedItem) {
    return [];
  }

  const stockItemType = registry.stockItemTypes[type];
  const finalActions = actions
    .filter(
      (group) =>
        group.showIf == null ||
        group.showIf({ selectedItem, stockItemType, features })
    )
    .map((group) => {
      if (group.id === "articleActions") {
        group = {
          ...group,
          items: registry.getArticleActions(stockItemType.articleType),
        };
      }
      if (group.id === "stockActions") {
        group = {
          ...group,
          items: registry.getStockActions(type),
        };
      }
      return {
        ...group,
        items: group.items.filter(
          (item) =>
            item.showIf == null ||
            item.showIf({
              item: selectedItem,
              selectedItem,
              stockItemType,
              features,
            })
        ),
      };
    })
    .filter((group) => group.items.length > 0);

  return finalActions.flatMap((group) => [
    <Divider key={`${group.id}`} />,
    ...group.items.map((action, i) => {
      if (action.MenuItem) {
        return (
          <action.MenuItem
            key={`${group.id}.${i}`}
            dense
            item={selectedItem}
            article={selectedItem.article}
            onClick={handleItemAction(action.action ?? action)}
            onClose={handleHidePopover}
          />
        );
      }
      return (
        <MenuItem
          key={`${group.id}.${i}`}
          dense
          onClick={handleItemAction(action.action ?? action)}
          disabled={action?.disableIf?.({ getStock, selectedItem }) ?? false}
          style={action.Icon ? { paddingRight: 48 } : undefined}
        >
          {typeof action.label === "string"
            ? action.label
            : action.label({ t, registry, stockItemType, type })}
          {action.Icon && (
            <ListItemIcon style={{ position: "absolute", right: 0 }}>
              <action.Icon fontSize="small" />
            </ListItemIcon>
          )}
        </MenuItem>
      );
    }),
  ]);
}

class Stock extends React.PureComponent {
  state = {
    selectedItem: null,
    updateItemDialogOpen: false,
    anchorEl: null,
    actionMenuOpen: false,
    csvExportDialogOpen: false,
    aggregateExport: false,
    search: null,
    activeQuickFilters: JSON.parse(
      localStorage.getItem("skl-stockTable-quickfilters") || "{}"
    ),
  };

  searchRef = createRef();

  stockApi = {
    setSearchInput: (value) => {
      this.searchRef.current.setValue(value);
    },
  };

  handleRowClick = (e, rowId, row) => {
    if (this.props.tab === "archive") {
      // TODO maybe we can allow restoring a hidden stock item or the corresponding article here
      return;
    }

    const articleActions = this.props.registry.getArticleActions(
      this.props.registry.stockItemTypes[this.props.type].articleType
    );
    if (
      row.__typename === "AggregatedStockItems" &&
      articleActions.length === 0 &&
      this.props.registry.stockItemTypes[this.props.type].stockActionsConfig
        ?.canCreateStockItems === false
    ) {
      // no actions so don't display the menu
      return;
    }

    // every stock item has multiple actions (at least change stock and change location)
    // const actions = this.props.registry.getStockActions(stockItem.type);
    const hasMultipleActions = true; // actions.length > 0;
    const actionMenuPosition = {
      top: e.clientY,
      left: e.clientX,
    };
    this.setState({
      anchorEl: document.querySelector(`[data-row-id='${rowId}']`),
      selectedItem: row,
      updateItemDialogOpen: !hasMultipleActions,
      action: hasMultipleActions ? null : "changeStock",
      actionMenuOpen: hasMultipleActions,
      actionMenuPosition,
    });
  };

  handleItemAction = (action) => (e) => {
    if (action === "changeStock" || action.PopoverForm) {
      this.setState({
        updateItemDialogOpen: true,
        action,
        // sometimes the table rows re-render between the context menu opening and the action being clicked, so update the anchor element here
        anchorEl: document.querySelector(
          `[data-row-id='${this.state.selectedItem.id}']`
        ),
      });
    } else if (action === "hideItem") {
      this.handleHideItem(this.state.selectedItem);
    } else if (action === "createStockItem") {
      this.setState({
        updateItemDialogOpen: true,
        action: {
          PopoverForm: ({ item }) => (
            <CreateStockItemForm
              item={item}
              onSubmit={this.handleCreateItem}
              customFields={this.getCustomInputFields()}
            />
          ),
        },
        // sometimes the table rows re-render between the context menu opening and the action being clicked, so update the anchor element here
        anchorEl: document.querySelector(
          `[data-row-id='${this.state.selectedItem.id}']`
        ),
      });
    } else if (action === "copyStockItem") {
      this.setState({
        updateItemDialogOpen: true,
        action: {
          PopoverForm: ({ item }) => (
            <CreateStockItemForm
              item={item}
              defaultValue={item}
              onSubmit={this.handleCreateItem}
              customFields={this.getCustomInputFields()}
            />
          ),
        },
        // sometimes the table rows re-render between the context menu opening and the action being clicked, so update the anchor element here
        anchorEl: document.querySelector(
          `[data-row-id='${this.state.selectedItem.id}']`
        ),
      });
    }
    this.setState({ actionMenuOpen: false });
  };

  handleCreateItem = async (
    article,
    initialStock,
    location,
    note,
    customFieldValues
  ) => {
    const { t } = this.props;
    try {
      const actualInitialStock =
        this.props.registry.stockItemTypes[
          this.props.type
        ].stockInfo.deltaToActual?.(initialStock, article) ?? initialStock;
      await this.props.onCreateItem(
        article.id,
        {
          initialStock: actualInitialStock,
          loadCarrierId: location?.loadCarrier,
          compartmentId: location?.compartment,
          customFields:
            customFieldValues.length > 0 ? customFieldValues : undefined,
          note,
        },
        note
      );
      this.handleHidePopover();
      this.props.snackbar.showMessage(
        t("Die {{type}}-Einheit wurde hinzugefügt", {
          type: this.props.registry.stockItemTypes[this.props.type].displayName(
            1,
            { t }
          ),
          context: this.props.type,
        })
      );
    } catch (e) {
      console.error(e);
      this.props.snackbar.showMessage(
        t("Die {{type}}-Einheit konnte nicht hinzugefügt werden werden", {
          type: this.props.registry.stockItemTypes[this.props.type].displayName(
            1,
            { t }
          ),
          context: this.props.type,
        })
      );
    }
  };

  handleUpdateItem = async (item, { delta, note, customFieldValues }) => {
    const { t } = this.props;
    try {
      await this.props.onUpdateItem(item, {
        delta,
        note,
        customFields:
          customFieldValues.length > 0 ? customFieldValues : undefined,
      });
      this.handleHidePopover();
      this.props.snackbar.showMessage(
        t("Der Bestand wurde aktualisiert"),
        t("Rückgängig"),
        async () => {
          try {
            await this.props.onUpdateItem(item, { delta: -delta });
          } catch (e) {
            console.error(e);
            this.props.snackbar.showMessage(
              t("Die Bestandsänderung konnte nicht rückgängig gemacht werden")
            );
          }
        }
      );
    } catch (e) {
      console.error(e);
      this.props.snackbar.showMessage(
        t("Der Bestand konnte nicht aktualisiert werden")
      );
    }
  };

  handleHideItem = async (item) => {
    const { t } = this.props;
    try {
      await this.props.onHideStockItem(item);
      this.props.snackbar.showMessage(
        t("Die {{type}}-Einheit wurde entfernt", {
          type: this.props.registry.stockItemTypes[this.props.type].displayName(
            1,
            { t }
          ),
          context: this.props.type,
        })
      );
    } catch (e) {
      console.error(e);
      this.props.snackbar.showMessage(
        t("Die {{type}}-Einheit konnte nicht entfernt werden", {
          type: this.props.registry.stockItemTypes[this.props.type].displayName(
            1,
            { t }
          ),
          context: this.props.type,
        })
      );
    }
  };

  handleHidePopover = () =>
    this.setState({
      updateItemDialogOpen: false,
      actionMenuOpen: false,
      csvExportDialogOpen: false,
    });

  handleSaveNote = async (e, { note, noteIndex, id, type }) => {
    const { t } = this.props;
    try {
      let previousNote;
      if (type === "stockItem") {
        const item = this.props.getStockItemById(this.props.stock, id);
        await this.props.onUpdateStockItemNote(item, noteIndex, note);
        previousNote =
          item[`note${noteIndex === 0 ? "" : noteIndex + 1}`]?.text ?? "";
      } else if (type === "article") {
        const item = this.props.stock.find((row) => row.article.id === id);
        await this.props.onUpdateArticleNote(item, noteIndex, note);
        previousNote =
          item.article[`note${noteIndex === 0 ? "" : noteIndex + 1}`]?.text ??
          "";
      } else {
        return;
      }
      this.props.snackbar.showMessage(
        note === ""
          ? t("Die Notiz wurde entfernt")
          : t("Die Notiz wurde aktualisiert"),
        t("Rückgängig"),
        async () => {
          try {
            if (type === "stockItem") {
              const item = this.props.getStockItemById(this.props.stock, id);
              await this.props.onUpdateStockItemNote(
                item,
                noteIndex,
                previousNote
              );
            } else if (type === "article") {
              const item = this.props.stock.find(
                (row) => row.article.id === id
              );
              await this.props.onUpdateArticleNote(
                item,
                noteIndex,
                previousNote
              );
            }
          } catch (e) {
            console.error(e);
            this.props.snackbar.showMessage(
              t("Die Notizänderung konnte nicht rückgängig gemacht werden")
            );
          }
        }
      );
    } catch (e) {
      console.error(e);
      this.props.snackbar.showMessage(
        t("Die Notiz konnte nicht gespeichert werden")
      );
    }
  };

  handleSaveNoteColumnTitle = async (e, { noteIndex, title, type }) => {
    const { t } = this.props;
    try {
      if (type === "stockItem") {
        await this.props.onUpdateStockItemNoteColumnTitle(noteIndex, title);
      } else if (type === "article") {
        await this.props.onUpdateArticleNoteColumnTitle(noteIndex, title);
      }
    } catch (e) {
      console.error(e);
      this.props.snackbar.showMessage(
        t("Der Spaltentitel konnte nicht gespeichert werden")
      );
    }
  };

  getColumns = () => {
    const {
      registry,
      articleNoteColumns,
      stockItemNoteColumns,
      type,
      t,
      features,
      fields,
      hiddenColumns,
      additionalColumns,
    } = this.props;

    let customFieldsKey = "";
    const columns = fields
      .filter(({ asField }) => asField == null)
      .map((field) => {
        customFieldsKey += field.id;
        return registry.customFieldType[field.type]?.getColumn(field, { t });
      });

    return {
      columns: [
        ...registry.getStockTableConfig(type, { t }),
        compartmentColumn({ t }),
        towerColumn({ t }),
        features.loadCarriers ? loadCarrierColumn({ t }) : undefined,
        {
          id: "temporaryLocation",
          title: t("Temporärer Lagerort"),
          contentString: (row) => {
            if (row.temporaryLocation) {
              let content = row.temporaryLocation;
              if (row.temporaryCompartment) {
                content += `, ${row.temporaryCompartment}`;
              }
              return content;
            }
            return row.temporaryCompartment || "";
          },
          compare: emptyToBottom(
            maybeAggregated((row) => {
              if (row.temporaryLocation) {
                let content = row.temporaryLocation;
                if (row.temporaryCompartment) {
                  content += `, ${row.temporaryCompartment}`;
                }
                return content;
              }
              return row.temporaryCompartment || "";
            })
          ),
          filter: {
            type: StringSearchFilter,
          },
        },
        ...columns,
        ...(additionalColumns?.({ t, registry, type }) ?? []),
        articleNoteColumn(
          {
            id: "note",
            title: articleNoteColumns?.[0],
            note: (row) => row.article.note,
            noteIndex: 0,
          },
          { t }
        ),
        articleNoteColumn(
          {
            id: "note2",
            title: articleNoteColumns?.[1],
            note: (row) => row.article.note2,
            noteIndex: 1,
          },
          { t }
        ),
        articleNoteColumn(
          {
            id: "note3",
            title: articleNoteColumns?.[2],
            note: (row) => row.article.note3,
            noteIndex: 2,
          },
          { t }
        ),
        stockItemNoteColumn(
          {
            id: "stockItemNote",
            title: stockItemNoteColumns?.[0],
            noteIndex: 0,
          },
          { t }
        ),
        stockItemNoteColumn(
          {
            id: "stockItemNote2",
            title: stockItemNoteColumns?.[1],
            noteIndex: 1,
          },
          { t }
        ),
        stockItemNoteColumn(
          {
            id: "stockItemNote3",
            title: stockItemNoteColumns?.[2],
            noteIndex: 2,
          },
          { t }
        ),
      ]
        .filter((column) => !!column)
        .filter((column) => !hiddenColumns?.includes(column.id)),
      tableKey: `${type}-${customFieldsKey}`,
    };
  };

  stockItemToStrings = (item, columns, rawValue = true) =>
    columns
      .filter((c) => c.export == null || !c.export.exclude)
      .map((c) => {
        if (rawValue !== false && c.export && c.export.getRawValue) {
          return c.export.getRawValue(item);
        }
        if (c.contentString) {
          return c.contentString(item);
        } else if (c.content) {
          return c.content(item);
        } else {
          return item[c.id];
        }
      })
      .map((value) => (value == null ? "" : value.toString()));

  stockItemToExcel = (item, columns) =>
    columns
      .filter((c) => c.export == null || !c.export.exclude)
      .map((c) => {
        if (c.export && c.export.getRawValue) {
          return c.export.getRawValue(item);
        }
        if (c.contentString) {
          return c.contentString(item);
        } else if (c.content) {
          return c.content(item);
        } else {
          return item[c.id];
        }
      })
      .map((value) => (value == null ? "" : value));

  handleExport = async (columnIds, filename, format) => {
    this.handleHidePopover();

    const { stock } = this.props;
    const columns = this.getColumns().columns.filter(
      (c) => columnIds.includes(c.id) && (c.export == null || !c.export.exclude)
    );

    if (format === "csv") {
      const stockRows = this.state.aggregateExport
        ? stock.map((item) =>
            item.stockItems.length === 1
              ? this.stockItemToStrings(item.stockItems[0], columns)
              : this.stockItemToStrings(item, columns)
          )
        : stock
            .flatMap((item) => item.stockItems)
            .map((item) => this.stockItemToStrings(item, columns));

      saveCsv(
        [columns.map((c) => c.export?.title ?? c.title), ...stockRows],
        filename
      );
    } else if (format === "xlsx") {
      const stockRows = this.state.aggregateExport
        ? stock.map((item) =>
            item.stockItems.length === 1
              ? this.stockItemToExcel(item.stockItems[0], columns)
              : this.stockItemToExcel(item, columns)
          )
        : stock
            .flatMap((item) => item.stockItems)
            .map((item) => this.stockItemToExcel(item, columns));

      saveXlsx(
        [columns.map((c) => c.export?.title ?? c.title), ...stockRows],
        filename,
        this.props.t("Bestand")
      );
    }
  };

  getFixedColumnStyles = ({ row, columnIndex, fixedColumnsCount }) => {
    const customField = this.props.fields.find(
      (field) => field.type === "color" && field.useAsTableRowColor
    );
    const background = customField
      ? row?.article.customFields.find(
          (field) => field.customFieldId === customField.id
        )?.value
      : undefined;
    if (!background) {
      return undefined;
    }
    if (columnIndex === fixedColumnsCount - 1) {
      return {
        background: `linear-gradient(270deg, ${fade(
          background,
          0
        )}, ${background} 8px)`,
        color: this.props.theme.palette.getContrastText(background),
      };
    }
    return {
      background,
      color: this.props.theme.palette.getContrastText(background),
    };
  };

  handleChangeSearch = debounce((search) => {
    this.setState({ search });
  }, 100);

  handleToggleQuickFilter = (filterId) => () => {
    const activeQuickFilters = {
      ...this.state.activeQuickFilters,
    };
    if (filterId in activeQuickFilters) {
      delete activeQuickFilters[filterId];
    } else {
      activeQuickFilters[filterId] = true;
    }
    this.setState({ activeQuickFilters });
    localStorage.setItem(
      "skl-stockTable-quickfilters",
      JSON.stringify(activeQuickFilters)
    );
  };

  applyQuickFilters = (row) => {
    const quickFilters = this.props.quickFilters || defaultQuickFilters;
    const stockItemType = this.props.registry.stockItemTypes[this.props.type];
    return quickFilters
      .filter((filter) => this.state.activeQuickFilters[filter.id])
      .every((filter) =>
        filter.filter({
          row,
          getStock:
            stockItemType.stockInfo.calculateStock ??
            ((item) => item.initial + item.in - item.out),
        })
      );
  };

  getFilteredStockItems = (columns) => {
    const { search } = this.state;
    const { filters, searchTerms } = prepareSmartSearch(search);

    return (row) => {
      if (!this.applyQuickFilters(row)) {
        return false;
      }

      if (filters.some(({ isMatch }) => !isMatch(row))) {
        return false;
      }

      if (this.props.features.strictStockSearch) {
        // at least one column must contain all search terms
        return this.stockItemToStrings(row, columns, false).some((value) =>
          searchTerms.every(
            (s) => value.toString().toLowerCase().indexOf(s.toLowerCase()) >= 0
          )
        );
      } else {
        // every search term must be contained in at least one column
        return searchTerms.every((s) =>
          this.stockItemToStrings(row, columns, false).some(
            (value) =>
              value.toString().toLowerCase().indexOf(s.toLowerCase()) >= 0
          )
        );
      }
    };
  };

  handleSaveCustomField = async (value, customFieldId, stockId) => {
    const { t } = this.props;

    try {
      const customField = this.props.fields.find(
        (field) => field.id === customFieldId
      );

      let ids = {};
      if (customField.articleType) {
        const stock = this.props.getStockItemById(this.props.stock, stockId);
        ids.articleId = stock.article?.id;
      } else if (customField.stockItemType) {
        ids.stockItemId = stockId;
      } else if (customField.stockReservationType) {
        ids.stockReservationId = stockId;
      } else if (customField.stockOrderType) {
        ids.stockOrderId = stockId;
      }

      await this.props.onUpdateCustomFieldValue(customFieldId, ids, value);
    } catch (e) {
      console.error(e);
      const { fields } = this.props;
      const fieldDefinition = fields.find(
        (field) => field.id === customFieldId
      );
      const name = fieldDefinition
        ? fieldDefinition.name
        : `mit der internen ID ${customFieldId}`;
      this.props.snackbar.showMessage(
        t("Das benutzerdefinierte Feld konnte nicht gespeichert werden", {
          name,
        })
      );
    }
  };

  getCustomInputFields = () => {
    const { registry, fields, t } = this.props;

    return fields.map((field) => {
      return {
        CustomField: registry.customFieldType[field.type].getInputField(field, {
          t,
        }),
        customFieldId: field.id,
        asField: field.asField,
        defaultValue: field.defaultValue,
        target: field.stockItemType !== null ? "stockItem" : "article",
      };
    });
  };

  getChangeStockCustomInputFields = () => {
    const { registry, changeStockFields, t } = this.props;

    return changeStockFields.map((field) => {
      return {
        CustomField: registry.customFieldType[field.type].getInputField(field, {
          t,
        }),
        customFieldId: field.id,
        asField: field.asField,
        defaultValue: field.defaultValue,
        target: "stockItem",
      };
    });
  };

  render() {
    const {
      classes,
      stock,
      articleNoteColumns,
      stockItemNoteColumns,
      fields,
      type,
      tab,
      onChangeTab,
      registry,
      error,
      loading,
      t,
      features,
      quickFilters = defaultQuickFilters,
      Placeholder = StockPlaceholder,
      actions = defaultActions,
      hideStockToolbarActions = false,
      pageToolbarActions,
      configKey = type,
      fixedColumns = 2,
    } = this.props;

    const {
      anchorEl,
      action,
      selectedItem,
      updateItemDialogOpen,
      actionMenuOpen,
      actionMenuPosition,
      csvExportDialogOpen,
      aggregateExport,
      activeQuickFilters,
    } = this.state;

    const { columns, tableKey } = this.getColumns({
      t,
      registry,
      type,
      articleNoteColumns,
      stockItemNoteColumns,
      fields,
    });
    const customInputFields = this.getCustomInputFields();
    const changeStockCustomInputFields = this.getChangeStockCustomInputFields();

    const stockItemType = this.props.registry.stockItemTypes[type];
    const getStock =
      stockItemType.stockInfo.calculateStock ??
      ((item) => item.initial + item.in - item.out);

    return (
      <div className={classes.root}>
        <div className={classes.toolbar}>
          <SearchTextField
            innerRef={this.searchRef}
            onChange={this.handleChangeSearch}
            placeholder={t("Suchen…")}
            className={classes.search}
          />
          {!hideStockToolbarActions && features.archivedStockTab && (
            <>
              <div className={classes.flex} />
              <ToggleButton
                value={tab === "active"}
                onClick={() => onChangeTab("active")}
                className={classes.toolbarButton}
              >
                <Trans>Aktive Bestände</Trans>
              </ToggleButton>
              <ToggleButton
                value={tab === "archive"}
                onClick={() => onChangeTab("archive")}
                className={classes.toolbarButton}
              >
                <Trans>Archivierte Bestände</Trans>
              </ToggleButton>
            </>
          )}
          <div className={classes.flex} />
          <ComponentExtensionPoint name="stockTableToolbar" variant="list" />
          {quickFilters.length > 0 && (
            <PopoverIconButton
              className={classes.toolbarButton}
              icon={
                Object.keys(activeQuickFilters).length > 0
                  ? Filter
                  : FilterOutline
              }
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              tooltip={t("Schnellfilter")}
              color={
                Object.keys(activeQuickFilters).length > 0
                  ? "primary"
                  : "default"
              }
            >
              <MenuList dense>
                {quickFilters.map((filter) => (
                  <MenuItem
                    key={filter.id}
                    onClick={this.handleToggleQuickFilter(filter.id)}
                    dense
                  >
                    <ListItemIcon>
                      <Check
                        style={{
                          opacity: activeQuickFilters[filter.id] ? 1 : 0,
                        }}
                      />
                    </ListItemIcon>
                    <Typography variant="inherit">
                      {filter.label({ t })}
                    </Typography>
                  </MenuItem>
                ))}
              </MenuList>
            </PopoverIconButton>
          )}
          <TooltipIconButton
            tooltip={t("Exportieren")}
            className={classes.toolbarButton}
            onClick={(e) => {
              this.setState({
                csvExportDialogOpen: true,
                anchorEl: e.target,
              });
            }}
          >
            <TableArrowDown />
          </TooltipIconButton>
          {!hideStockToolbarActions &&
            registry.getStockToolbarActions(type).map((action) => {
              const label = action.label({ t });
              const Button = ({ onClick }) => (
                <TooltipIconButton
                  tooltip={label}
                  className={classes.toolbarButton}
                  onClick={onClick}
                >
                  <action.Icon />
                </TooltipIconButton>
              );
              return (
                <action.Component
                  key={label}
                  Button={Button}
                  stockApi={this.stockApi}
                />
              );
            })}
          {pageToolbarActions?.map((action) => {
            const label = action.label({ t });
            const Button = ({ onClick }) => (
              <TooltipIconButton
                tooltip={label}
                className={classes.toolbarButton}
                onClick={onClick}
              >
                <action.Icon />
              </TooltipIconButton>
            );
            return (
              <action.Component
                key={label}
                Button={Button}
                pageApi={this.stockApi}
              />
            );
          })}
        </div>
        <StockTable
          key={tableKey}
          className={classes.table}
          localStorageKey={`skl-stockTable-${configKey}`}
          columns={columns}
          data={loading ? null : stock}
          nestedRowsField="stockItems"
          flattenSingleItemGroups
          rowFilter={this.getFilteredStockItems(columns)}
          order="asc"
          orderBy="sku"
          onRowClick={this.handleRowClick}
          rowId={(row) => row.id}
          rowRenderer={({ key, ...props }, { row, nestedOf }) => {
            const hasMinimumStock = ifStockItem(
              (row) => {
                return (
                  (nestedOf == null || nestedOf.flattened) && // nur in aggregierten Zeilen anzeigen
                  row.article.type === type &&
                  row.article.minimumStock != null &&
                  (features.minimumStockByPlannedStock
                    ? getStock(row) + row.ordered - row.reserved <=
                      row.article.minimumStock
                    : getStock(row) <= row.article.minimumStock)
                );
              },
              (row) => {
                if (features.minimumStockByPlannedStock) {
                  const planned = row.stockItems.reduce(
                    (sum, stockItem) =>
                      sum +
                      stockItem.initial +
                      stockItem.in -
                      stockItem.out +
                      stockItem.ordered -
                      stockItem.reserved,
                    0
                  );
                  return (
                    !isNaN(planned) &&
                    features.minimumStockByPlannedStock &&
                    row.article.minimumStock != null &&
                    planned <= row.article.minimumStock
                  );
                }

                const current =
                  getStock(row) ||
                  row.stockItems.reduce(
                    (sum, stockItem) => sum + getStock(stockItem),
                    0
                  );
                return (
                  !isNaN(current) &&
                  row.article.minimumStock != null &&
                  current <= row.article.minimumStock
                );
              }
            )(row);

            return (
              <ComponentExtensionPoint
                key={key}
                name="stockTableRow"
                innerProps={{ ...props, row, nestedOf, hasMinimumStock }}
                InnerComponent={({
                  row,
                  nestedOf,
                  hasMinimumStock,
                  ...props
                }) => (
                  <TableRow
                    {...props}
                    data-row-id={row.id}
                    className={cx(props.className, {
                      [classes.minimumStockRow]: hasMinimumStock,
                    })}
                  />
                )}
                variant="nested"
              />
            );
          }}
          cellContentParams={{
            handleSaveNote: this.handleSaveNote,
            handleSaveCustomField: this.handleSaveCustomField,
            features,
          }}
          headerActionsParams={{
            handleSaveNoteColumnTitle: this.handleSaveNoteColumnTitle,
          }}
          size="small"
          EnhancedTableHeadProps={{ size: "medium" }}
          disableLineWrap
          maxColumnWidth={170 + 32}
          highlightedRows={
            actionMenuOpen || updateItemDialogOpen
              ? [selectedItem.id]
              : undefined
          }
          placeholder={
            <TablePlaceholder>
              <Placeholder error={error} type={type} tab={tab} />
            </TablePlaceholder>
          }
          fixedColumnStyles={this.getFixedColumnStyles}
          fixedColumns={fixedColumns}
        />
        <Menu
          open={actionMenuOpen}
          onClose={this.handleHidePopover}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          anchorReference="anchorPosition"
          anchorPosition={
            actionMenuPosition
              ? {
                  top: actionMenuPosition.top - 24 - 16,
                  left: actionMenuPosition.left - 24,
                }
              : null
          }
        >
          <ListSubheader className={classes.menuHeader}>
            {[
              selectedItem?.article.name,
              selectedItem?.sku ?? selectedItem?.article.sku,
            ]
              .filter((text) => !!text?.trim())
              .join(" · ")}
          </ListSubheader>
          {getStockActions(actions, {
            selectedItem,
            getStock,
            t,
            registry,
            features,
            type,
            handleItemAction: this.handleItemAction,
            handleHidePopover: this.handleHidePopover,
          })}
        </Menu>
        <Popover
          open={updateItemDialogOpen}
          onClose={this.handleHidePopover}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          classes={{ paper: classes.deltaPopover }}
          PaperProps={{
            component: DraggablePaper,
            DraggableProps: {
              handle: `.${classes.deltaPopover} > *:first-child`,
              defaultClassNameDragging: classes.popoverDragging,
            },
          }}
        >
          {selectedItem && action === "changeStock" && (
            <ChangeStockForm
              item={selectedItem}
              aggregatedItem={
                selectedItem != null
                  ? stock.find(
                      (aggregatedStockItem) =>
                        aggregatedStockItem.article.id ===
                        selectedItem.article.id
                    )
                  : null
              }
              onSubmit={this.handleUpdateItem}
              customFields={changeStockCustomInputFields}
            />
          )}
          {selectedItem && action && action !== "changeStock" && (
            <action.PopoverForm
              item={selectedItem}
              article={selectedItem.article}
              customFields={customInputFields}
              onClose={this.handleHidePopover}
            />
          )}
        </Popover>
        <Popover
          open={csvExportDialogOpen}
          onClose={this.handleHidePopover}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          classes={{ paper: classes.exportPopover }}
        >
          <TableExportForm
            columns={columns}
            onSubmit={this.handleExport}
            defaultFilename={t("Bestand {{type}} {{date}}", {
              type: registry.stockItemTypes[type].displayName(2, { t }),
              date: new Date().toLocaleDateString(),
            })}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={aggregateExport}
                  onChange={(e) =>
                    this.setState({ aggregateExport: e.target.checked })
                  }
                />
              }
              label={t("Zeilen nach Artikel gruppieren")}
              className={classes.aggregateExport}
            />
          </TableExportForm>
        </Popover>
      </div>
    );
  }
}

export default withTranslation()(
  withStyles(styles, { withTheme: true })(withRegistry()(withSnackbar()(Stock)))
);
